import ReactECharts from 'echarts-for-react';

import data from '../../data/warmVsCold.json';
import { useIsDark } from '../../useIsDark';

/**
 * blog25's figures — THREE interactive ECharts components reading ONE committed data file.
 *
 * CHART TYPE FIRST (RULES_BLOG_POST): each figure answers exactly one question, and the type is
 * chosen for that question before any styling.
 *
 *   WarmVsColdFlowChart    where investments actually come from   5 ranked shares -> horizontal bar
 *   WarmVsColdDoorChart    same deck, different door              2 channels x 2 measures -> grouped bar
 *   WarmVsColdLeverChart   which cold levers still work           signed lifts, one of them a null -> vertical bar
 *
 * Data: `../../data/warmVsCold.json`, the FE twin of the committed source-of-record CSV at
 * `playano-docs/raisy/projects/blog/assets/blog25/warm_vs_cold_outreach.csv` (Rule 27). No number is
 * typed into this file, and the confidence tier travels with every row so a figure can state how
 * sure we are instead of implying every number is equally solid.
 *
 * Theme: `dark` prop override, otherwise follows the live html class theme
 * (RULES_BLOG_POST Rule 29).
 */

type Row = {
  series: string;
  label: string;
  value: number;
  unit: string;
  display: string;
  source: string;
  confidence: string;
  note: string;
};

const ROWS = data.rows as Row[];
const rowsOf = (series: string): Row[] => ROWS.filter((r) => r.series === series);

/** The units this post measures. Kept narrow on purpose: a formatter that accepts anything formats nothing. */
type Unit = 'pct' | 'pct_meeting' | 'seconds' | 'pct_lift' | 'pct_lift_opens';

const BRAND = {
  teal: '#0AB3BF',
  tealLight: '#4DD4E0',
  tealDeep: '#087B8B',
  green: '#10B981',
  slate: '#64748B',
  red: '#EF4444',
  axis: '#334155',
  muted: '#64748B',
};

/**
 * The ONE formatter (Rule 21). Every tooltip, axis tick and bar label goes through it.
 *
 * `role` exists instead of a separate axis lambda so there is exactly one implementation of "a
 * number becomes a string". An axis grid may be coarser than a bar label because a gridline is a
 * scale rather than a value; that is a readability choice, and it is still made HERE. An
 * independently written axis formatter is the defect this rule names, because the same figure then
 * renders one number two ways.
 */
const formatFor = (unit: Unit, v: number, role: 'label' | 'axis' = 'label'): string => {
  if (unit === 'seconds') {
    // m:ss in both roles, so a tick and a label can never be reconciled differently.
    return `${Math.floor(v / 60)}m${String(Math.round(v % 60)).padStart(2, '0')}s`;
  }
  if (unit === 'pct' || unit === 'pct_meeting') return `${Math.round(v)}%`;
  // A null lift is the finding, not a formatting edge case, so it is named rather than printed as 0.
  if (v === 0 && role === 'label') return 'no effect';
  const n = Number.isInteger(v) ? `${v}` : v.toFixed(1);
  return `+${n}%`;
};

const theme = (dark: boolean) =>
  dark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

/** Shared axis furniture so the three figures read as one series. */
const valueAxis = (
  unit: Unit,
  colour: string,
  name: string,
  position: 'left' | 'right',
  dark: boolean,
) => ({
  type: 'value',
  name,
  position,
  nameTextStyle: { color: theme(dark).muted, fontSize: 11 },
  axisLine: { show: false },
  // Only the left axis draws gridlines; two sets of them turns the plot into graph paper.
  splitLine: position === 'left' ? { lineStyle: { color: 'rgba(100,116,139,0.18)' } } : { show: false },
  axisLabel: { color: colour, fontSize: 11, formatter: (v: number) => formatFor(unit, v, 'axis') },
});

// ---------------------------------------------------------------------------------------------
// Figure 1 — where VC investments actually come from
// ---------------------------------------------------------------------------------------------

const FLOW_COLOUR: Record<string, string> = {
  'Professional networks': BRAND.teal,
  'Proactively self-generated': BRAND.tealLight,
  'Referrals from other investors': BRAND.tealDeep,
  'Referrals from portfolio companies': BRAND.green,
  'Cold inbound from founders': BRAND.red,
};

export const WarmVsColdFlowChart = ({
  height = 300,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const C = theme(isDark);
  // Ascending, because a category axis renders its first entry at the bottom: this puts the largest
  // channel on top, which is how a ranking should read.
  const rows = [...rowsOf('deal_flow')].sort((a, b) => a.value - b.value);
  const unit: Unit = 'pct';

  const option = {
    animationDuration: 600,
    backgroundColor: C.bg,
    grid: { left: 8, right: 56, top: 16, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(15,23,42,0.95)',
      borderColor: C.grid,
      textStyle: { color: '#E2E8F0', fontSize: 12 },
      formatter: (p: Array<{ name: string; value: number }>) =>
        `${p[0]?.name ?? ''}: <strong>${formatFor(unit, p[0]?.value ?? 0)}</strong>`,
    },
    xAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: 'rgba(100,116,139,0.18)' } },
      axisLabel: { color: BRAND.muted, fontSize: 11, formatter: (v: number) => formatFor(unit, v, 'axis') },
    },
    yAxis: {
      type: 'category',
      data: rows.map((r) => r.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
      axisLabel: { color: C.axis, fontSize: 12 },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 26,
        data: rows.map((r) => ({
          value: r.value,
          itemStyle: { color: FLOW_COLOUR[r.label] ?? BRAND.slate, borderRadius: [0, 4, 4, 0] },
        })),
        label: {
          show: true,
          position: 'right',
          color: C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { value: number }) => formatFor(unit, p.value),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Figure 1. Where venture investments actually come from. A survey of 885 venture capitalists at 678
        firms attributes about 30 percent of investments to professional networks, 30 percent to the
        investor&apos;s own proactive search, 20 percent to referrals from other investors, 8 percent to
        referrals from portfolio companies, and roughly 10 percent to cold inbound from founders. Cold is
        the smallest single slice, and it is the slice most fundraising tooling is built for. Source:
        Gompers, Gornall, Kaplan &amp; Strebulaev, Journal of Financial Economics, 2020. Peer-reviewed, so
        this figure carries high confidence. Shares are as published and total 98 because the survey
        rounds.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Figure 2 — same deck, different door
// ---------------------------------------------------------------------------------------------

export const WarmVsColdDoorChart = ({
  height = 300,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const C = theme(isDark);
  const conversion = rowsOf('conversion');
  const attention = rowsOf('attention').filter((r) => r.label !== 'Average deck read (all)');
  const coldConv = conversion.find((r) => r.label.includes('Cold'))?.value ?? 0;
  const warmConv = conversion.find((r) => r.label.includes('Warm'))?.value ?? 0;
  const coldAtt = attention.find((r) => r.label.includes('cold'))?.value ?? 0;
  const warmAtt = attention.find((r) => r.label.includes('warm'))?.value ?? 0;

  // The two measures carry different units, so each gets its own axis and the reader only ever
  // compares cold against warm WITHIN a unit. Four series with nulls keeps the pairs on separate
  // axes instead of drawing two units against one scale.
  const option = {
    animationDuration: 600,
    backgroundColor: C.bg,
    grid: { left: 8, right: 8, top: 40, bottom: 8, containLabel: true },
    legend: {
      top: 0,
      textStyle: { color: C.muted, fontSize: 11 },
      data: ['Cold: deck to meeting', 'Warm: deck to meeting', 'Cold: review time', 'Warm: review time'],
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(15,23,42,0.95)',
      borderColor: '#334155',
      textStyle: { color: '#E2E8F0', fontSize: 12 },
      formatter: (p: Array<{ seriesName: string; value: number | null }>) =>
        p
          .filter((x) => x.value !== null && x.value !== undefined)
          .map((x) => {
            const unit: Unit = x.seriesName.includes('meeting') ? 'pct_meeting' : 'seconds';
            return `${x.seriesName}: <strong>${formatFor(unit, x.value as number)}</strong>`;
          })
          .join('<br/>'),
    },
    xAxis: {
      type: 'category',
      data: ['Deck to meeting', 'Deck review time'],
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
      axisLabel: { color: C.axis, fontSize: 12 },
    },
    yAxis: [
      valueAxis('pct_meeting', BRAND.slate, 'meeting conversion (%)', 'left', isDark),
      valueAxis('seconds', BRAND.muted, 'deck review time', 'right', isDark),
    ],
    series: [
      {
        name: 'Cold: deck to meeting',
        type: 'bar',
        yAxisIndex: 0,
        barMaxWidth: 44,
        data: [coldConv, null],
        itemStyle: { color: BRAND.slate, borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { value: number }) => formatFor('pct_meeting', p.value),
        },
      },
      {
        name: 'Warm: deck to meeting',
        type: 'bar',
        yAxisIndex: 0,
        barMaxWidth: 44,
        data: [warmConv, null],
        itemStyle: { color: BRAND.teal, borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { value: number }) => formatFor('pct_meeting', p.value),
        },
      },
      {
        name: 'Cold: review time',
        type: 'bar',
        yAxisIndex: 1,
        barMaxWidth: 44,
        data: [null, coldAtt],
        itemStyle: { color: BRAND.slate, borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { value: number }) => formatFor('seconds', p.value),
        },
      },
      {
        name: 'Warm: review time',
        type: 'bar',
        yAxisIndex: 1,
        barMaxWidth: 44,
        data: [null, warmAtt],
        itemStyle: { color: BRAND.teal, borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { value: number }) => formatFor('seconds', p.value),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Figure 2. Same deck, different door. A deck arriving through a warm introduction is read for about
        four minutes eighteen seconds against two minutes thirty-one seconds cold, and converts to a meeting
        at roughly 40 to 50 percent against 3 to 5 percent. The bars plot the midpoint of each published
        range. The two measures have different units, so each keeps its own axis and the only comparison
        drawn is cold against warm within a measure. Source: DocSend deck analytics and its 2025 warm-intro
        synthesis. Platform data published as marketing research rather than peer review, so this figure
        carries medium confidence: the direction is corroborated, the exact multiple is not.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Figure 3 — the cold levers, and the one that died
// ---------------------------------------------------------------------------------------------

export const WarmVsColdLeverChart = ({
  height = 320,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const C = theme(isDark);
  // The two units are told apart in the category label rather than by a second formatter, and the
  // 75-100 word peak is deliberately absent: it is a response rate, not a lift, so plotting it here
  // would mix a level with a change.
  const rows: Array<{ label: string; unit: Unit; dead: boolean }> = [
    { label: 'One follow-up', unit: 'pct_lift', dead: false },
    { label: 'Specific personalization', unit: 'pct_lift', dead: false },
    { label: 'Grade-3 reading level', unit: 'pct_lift', dead: false },
    { label: 'First-name merge 2016 (opens)', unit: 'pct_lift_opens', dead: false },
    { label: 'First-name merge 2024 (opens)', unit: 'pct_lift_opens', dead: true },
  ];
  const byLabel = (needle: string) => rowsOf('cold_levers').find((r) => r.label.includes(needle));

  const order = [
    byLabel('follow-up'),
    byLabel('Personalization'),
    byLabel('Grade-3'),
    byLabel('Name-merge personalization (2016)'),
    byLabel('Name-merge personalization (2024)'),
  ];

  const option = {
    animationDuration: 600,
    backgroundColor: C.bg,
    grid: { left: 8, right: 16, top: 32, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(15,23,42,0.95)',
      borderColor: '#334155',
      textStyle: { color: '#E2E8F0', fontSize: 12 },
      // The unit is read from the category the cursor is on, not guessed from the label text: a
      // startsWith match against the published label broke the moment one was abbreviated.
      formatter: (p: Array<{ dataIndex: number; name: string; value: number }>) => {
        const i = p[0]?.dataIndex ?? 0;
        return `${p[0]?.name ?? ''}: <strong>${formatFor(rows[i].unit, p[0]?.value ?? 0)}</strong>`;
      },
    },
    xAxis: {
      type: 'category',
      data: rows.map((r) => r.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
      axisLabel: { color: C.axis, fontSize: 11, interval: 0, width: 92, overflow: 'break' },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: 'rgba(100,116,139,0.18)' } },
      axisLabel: {
        color: BRAND.muted,
        fontSize: 11,
        formatter: (v: number) => formatFor(rows[1].unit, v, 'axis'),
      },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 62,
        data: rows.map((r, i) => ({
          value: order[i]?.value ?? 0,
          itemStyle: { color: r.dead ? BRAND.red : BRAND.teal, borderRadius: [4, 4, 0, 0] },
        })),
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { dataIndex: number; value: number }) =>
            formatFor(rows[p.dataIndex].unit, p.value),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Figure 3. The cold levers, and the one that died. One follow-up lifts replies by 65.8 percent and
        specific personalization by about 142 percent, against 7 to 9 percent for basic mail-merge. A
        third-grade reading level lifts responses 36 percent. First-name merge fields lifted opens 20 percent
        in a 2016 randomized trial and nothing in their 2024 replication, which is why that bar is red and
        labelled as no effect. The two name-merge bars measure opens; the other three measure replies, and
        the category labels carry that difference rather than a second axis. Sources: Backlinko/Pitchbox
        12 million emails, Boomerang 40 million, Woodpecker 20 million and over, all large vendor datasets,
        so medium confidence; the 2016 trial is Sahni, Wheeler and Chintagunta, Marketing Science, 2016,
        with the 2024 replication by Defau.
      </p>
    </div>
  );
};
